import type { Translation } from './en'

// Greek funnel copy. Typed as `Translation`, so any key the English file has and
// this one doesn't is a compile error. Same guardrails apply: no personality
// types, no scores, no invented social proof.
//
// `Copy` keeps the English *shape* — every key, nested exactly as declared — while
// widening the `as const` string literals back to plain strings. A missing or
// misspelled key is a compile error; a different Greek sentence is not.
type Copy<T> = T extends string ? string : { -readonly [K in keyof T]: Copy<T[K]> }

export const el: Copy<Translation> = {
  brand: 'Bonded',
  language: 'EN',
  back: 'Πίσω',
  continue: 'Συνέχεια',
  skip: 'Παράλειψη',
  loading: 'Ετοιμάζουμε μια καλύτερη στιγμή…',
  retry: 'Δοκιμάστε ξανά',
  stepOf: 'Ερώτηση {{n}} από {{total}}',

  consent: {
    title: 'Μια μικρή επιλογή πριν ξεκινήσουμε',
    body: 'Αποθηκεύουμε μόνο όσα χρειάζεται η σελίδα για να λειτουργεί. Με την άδειά σου θα μετρούσαμε και πώς χρησιμοποιείται η ροή, ώστε να τη βελτιώσουμε. Τίποτα προαιρετικό δεν φορτώνει αν δεν το επιλέξεις.',
    accept: 'Αποδοχή αναλυτικών',
    reject: 'Μόνο τα απαραίτητα',
    settings: 'Ρυθμίσεις cookies',
    cookiePolicy: 'Πολιτική Cookies',
    privacyPolicy: 'Πολιτική Απορρήτου',
  },

  footer: {
    copyright: '© {{year}} Bonded Studio. Powered by Sapphire Labs.',
  },

  landing: {
    cta: 'Φτιάξτε το πρώτο μας τελετουργικό',
    ctaWaitlist: 'Μπείτε στον Πρώτο Κύκλο',
    note: '90 δευτερόλεπτα · χωρίς πληρωμή',
    agreementPrefix: 'Συνεχίζοντας, συμφωνείτε με τους',
    terms: 'Όρους',
    and: 'και την',
    privacy: 'Πολιτική Απορρήτου',
    agreementError: 'Δεν μπορέσαμε να καταγράψουμε τη συγκατάθεσή σας. Δοκιμάστε ξανά.',
    researchOptIn:
      'Προαιρετικό: οι απαντήσεις μου να βοηθήσουν την έρευνα του Bonded για τη σύνδεση. Ανώνυμα, δεν πωλούνται ποτέ, και μπορείτε να τις αποσύρετε όποτε θέλετε.',
  },

  audiences: {
    'for-anyone': {
      pill: 'ΦΤΙΑΓΜΕΝΟ ΓΙΑ ΔΥΟ · ΠΡΩΤΑ Η ΠΡΟΩΡΗ ΠΡΟΣΒΑΣΗ',
      title: 'Μια εφαρμογή που ξεκινάει την κουβέντα.',
      sub: 'Ίδια ερώτηση, δύο κινητά. Διαλέξτε deck, ορίστε το βάθος, και αφήστε την εφαρμογή στην άκρη μόλις αρχίσετε να μιλάτε.',
    },
    'for-couples': {
      pill: 'ΓΙΑ ΖΕΥΓΑΡΙΑ · ΠΡΩΤΑ Η ΠΡΟΩΡΗ ΠΡΟΣΒΑΣΗ',
      title: 'Μια εφαρμογή που ξεκινάει την κουβέντα.',
      sub: 'Για τα βράδια που καταλήγουν σε πρακτικά. Μία ερώτηση που απαντάτε κι οι δύο μαζί — και κανείς δεν χρειάζεται να ανοιχτεί πρώτος.',
    },
    'for-first-dates': {
      pill: 'ΓΙΑ ΠΡΩΤΑ ΡΑΝΤΕΒΟΥ · ΠΡΩΤΑ Η ΠΡΟΩΡΗ ΠΡΟΣΒΑΣΗ',
      title: 'Μη σας τελειώσουν ποτέ τα θέματα.',
      sub: 'Ίδια ερώτηση, δύο κινητά. Χωρίς συνέντευξη, χωρίς σενάρια — και ο καθένας μπορεί να προσπεράσει ό,τι θέλει.',
    },
    'for-friends': {
      pill: 'ΓΙΑ ΦΙΛΟΥΣ · ΠΡΩΤΑ Η ΠΡΟΩΡΗ ΠΡΟΣΒΑΣΗ',
      title: 'Κάτι καλύτερο από τη συνηθισμένη κουβέντα.',
      sub: 'Ίδια ερώτηση, δύο κινητά. Πρώτα παιχνιδιάρικα, μετά προσωπικά — και το βάθος το ορίζετε εσείς.',
    },
  },

  visual: {
    answer: 'Απαντώ',
    pass: 'Πάσο',
    compare: {
      beforeTag: 'Τα περισσότερα βράδια',
      beforeQ: '«Πώς ήταν η μέρα σου;»',
      beforeA: '«Καλά. Εσύ;»',
      afterTag: 'Με το Bonded',
      afterQ: '«Για τι πράγμα άλλαξες γνώμη φέτος;»',
    },
    prompt: {
      tag: 'Το βλέπετε κι οι δύο',
      example: '«Ποιο μικρό πράγμα που κάνω δεν μου το έχεις πει ποτέ;»',
      caption: 'Το πάσο είναι πάντα εκεί. Κανείς δεν εξηγεί γιατί.',
    },
    pace: { label: 'Το βάθος σας', offTable: 'Εκτός συζήτησης' },
  },

  exit: {
    title: 'Ευχαριστούμε για την ειλικρίνεια.',
    body: 'Το Bonded είναι για ενήλικες. Θα είμαστε εδώ όταν γίνετε 18.',
  },

  questions: {
    connection_context: {
      title: 'Με ποιον είστε εδώ;',
      options: {
        partner: 'Το ταίρι μου',
        date: 'Ένα ραντεβού',
        friend: 'Έναν φίλο',
        new_person: 'Κάποιον καινούριο',
        unsure: 'Δεν είμαι σίγουρος/η ακόμα',
      },
    },
    age_band: {
      title: 'Και πόσων χρονών είστε;',
      body: 'Το Bonded είναι φτιαγμένο για ενήλικες.',
      options: {
        '18_24': '18–24',
        '25_34': '25–34',
        '35_44': '35–44',
        '45_54': '45–54',
        '55_64': '55–64',
        '65_plus': '65+',
        under_18: 'Είμαι κάτω των 18',
      },
    },
    together_length: {
      title: 'Πόσο καιρό είστε μαζί;',
      options: {
        under_1y: 'Λιγότερο από έναν χρόνο',
        '1_3y': '1–3 χρόνια',
        '3_7y': '3–7 χρόνια',
        over_7y: 'Πάνω από 7 χρόνια',
      },
    },
    dating_stage: {
      title: 'Σε ποιο σημείο είστε;',
      options: {
        not_met_yet: 'Δεν έχουμε γνωριστεί ακόμα',
        first_date_soon: 'Έρχεται το πρώτο ραντεβού',
        few_dates_in: 'Μετά από μερικά ραντεβού',
        just_started_seeing: 'Μόλις αρχίσαμε να βλεπόμαστε',
      },
    },
    friend_length: {
      title: 'Πόσο καιρό γνωρίζεστε;',
      options: {
        new_friend: 'Είμαστε καινούριοι φίλοι',
        few_years: 'Μερικά χρόνια',
        since_forever: 'Από πάντα',
      },
    },
    connection_goal: {
      title: 'Τι θα έκανε αυτή τη στιγμή να αξίζει;',
      options: {
        fun: 'Να γελάσουμε μαζί',
        closeness: 'Να νιώσουμε πιο κοντά',
        understanding: 'Να καταλάβουμε καλύτερα ο ένας τον άλλον',
        growth: 'Να πάμε κάπου πιο βαθιά',
      },
    },
    main_barrier: {
      title: 'Τι συνήθως μπαίνει εμπόδιο;',
      options: {
        questions: 'Δεν ξέρω τι να ρωτήσω',
        distraction: 'Κινητά και περισπασμοί',
        time_energy: 'Λίγος χρόνος ή ενέργεια',
        repetition: 'Οι ίδιες συζητήσεις',
        awkward_vulnerability: 'Το να ανοιχτώ με φέρνει σε αμηχανία',
        none: 'Τίποτα συγκεκριμένο',
      },
    },
    last_real_conversation: {
      title: 'Πότε μιλήσατε τελευταία φορά για κάτι πέρα από τα πρακτικά;',
      options: {
        this_week: 'Αυτή την εβδομάδα',
        this_month: 'Αυτόν τον μήνα',
        cant_remember: 'Ειλικρινά, δεν θυμάμαι',
        we_talk_like_this_often: 'Μιλάμε έτσι συχνά',
      },
    },
    first_date_worry: {
      title: 'Ποιο κομμάτι θα προτιμούσατε να αποφύγετε;',
      options: {
        running_out_of_things: 'Να μη μας μείνει τι να πούμε',
        feels_like_interview: 'Να μοιάζει με συνέντευξη',
        being_too_much: 'Να φανώ «υπερβολικός/ή»',
        nothing_really: 'Τίποτα ιδιαίτερο',
      },
    },
    catchup_pattern: {
      title: 'Πώς είναι συνήθως οι κουβέντες σας;',
      options: {
        same_topics: 'Τα ίδια θέματα κάθε φορά',
        we_go_deep: 'Πάμε αρκετά βαθιά',
        depends: 'Εξαρτάται από τη μέρα',
      },
    },
    intentional_time_frequency: {
      title: 'Πόσο συχνά βρίσκετε χρόνο σαν αυτόν;',
      options: {
        daily: 'Σχεδόν κάθε μέρα',
        weekly: 'Εβδομαδιαία',
        monthly: 'Μηνιαία',
        rarely: 'Σπάνια',
        first_time: 'Είναι κάτι καινούριο για εμάς',
      },
    },
    social_energy: {
      title: 'Τι ενέργεια έχετε αυτή τη στιγμή;',
      options: { soft: 'Ήπια', curious: 'Περίεργη', playful: 'Παιχνιδιάρικη', bold: 'Τολμηρή' },
    },
    intended_setting: {
      title: 'Πού θα το χρησιμοποιήσετε;',
      options: {
        home: 'Στο σπίτι',
        date: 'Σε ραντεβού',
        travelling_walking: 'Σε βόλτα ή ταξίδι',
        friends: 'Με φίλους',
        later: 'Το κρατάω για αργότερα',
      },
    },
    available_time: {
      title: 'Πόσος χρόνος σας ταιριάζει;',
      options: {
        '5_10': '5–10 λεπτά',
        '15_20': '15–20 λεπτά',
        '30_45': '30–45 λεπτά',
        flexible: 'Είμαστε ευέλικτοι',
      },
    },
    preferred_depth: {
      title: 'Πόσο βαθιά να πάει;',
      options: { '1': 'Ελαφριά', '2': 'Παιχνιδιάρικα', '3': 'Βαθιά', '4': 'Τολμηρά' },
    },
    topic_boundaries: {
      title: 'Υπάρχει κάτι που θα προτιμούσατε να μείνει έξω;',
      body: 'Προαιρετικό. Επιλέξτε όσα χρειάζεστε.',
      done: 'Τίποτα να μείνει έξω',
      options: {
        exes: 'Πρώην',
        money: 'Χρήματα',
        family: 'Οικογένεια',
        future: 'Το μέλλον',
        sex_intimacy: 'Σεξ & οικειότητα',
        religion: 'Θρησκεία',
        politics: 'Πολιτική',
        prefer_not_to_say: 'Προτιμώ να μην πω',
      },
    },
    scan_trigger: {
      title: 'Τι σας έκανε να σταματήσετε και να το δοκιμάσετε;',
      options: {
        social_video: 'Το είδα σε βίντεο',
        friend_told_me: 'Μου το είπε ένας φίλος',
        card: 'Μια κάρτα που μου έδωσαν',
        time_together: 'Έχουμε χρόνο μαζί',
        reconnecting: 'Θέλουμε να ξανασυνδεθούμε',
        repeated_conversation: 'Η κουβέντα επαναλαμβάνεται',
        first_meeting: 'Είναι πρώτη γνωριμία ή ραντεβού',
        curiosity: 'Απλή περιέργεια',
      },
    },
    return_value: {
      title: 'Τι θα έκανε το Bonded να αξίζει να το κρατήσετε;',
      options: {
        experiences: 'Νέες Εμπειρίες',
        guided_rituals: 'Καθοδηγούμενα τελετουργικά',
        memory_capsules: 'Κάψουλες Μνήμης',
        insights: 'Insights για τον χρόνο μας μαζί',
        unlimited_hosting: 'Απεριόριστη φιλοξενία',
      },
    },
  },

  value: {
    right_question: {
      eyebrow: 'Γιατί λειτουργεί',
      title: 'Η σωστή ερώτηση κάνει τη δουλειά.',
      body: 'Δεν είναι πρόβλημα εγγύτητας. Είναι πρόβλημα εκκίνησης.',
      cta: 'Συνεχίστε',
    },
    your_pace: {
      eyebrow: 'Πριν το φτιάξουμε',
      title: 'Τα όριά σας είναι μέρος του σχεδιασμού.',
      body: 'Ό,τι επισημάνατε μένει εκτός.',
      cta: 'Φτιάξτε το τελετουργικό μου',
    },
    reassurance: {
      cta: 'Βγάζει νόημα',
      variants: {
        comfort: {
          eyebrow: 'Σχετικά μ’ αυτό',
          title: 'Εσείς αποφασίζετε πόσο μακριά θα πάει.',
          body: 'Κάθε ερώτηση μπορεί να προσπεραστεί. Κανείς δεν εξηγεί γιατί.',
        },
        we_bring_questions: {
          eyebrow: 'Σχετικά μ’ αυτό',
          title: 'Δεν θα χρειαστεί ποτέ να σκεφτείτε ερώτηση.',
          body: 'Το Bonded φέρνει την ερώτηση. Εσείς απλώς την απαντάτε.',
        },
        ten_minutes: {
          eyebrow: 'Σχετικά μ’ αυτό',
          title: 'Δέκα λεπτά αρκούν.',
          body: 'Μία ερώτηση, κι οι δυο σας. Αυτό είναι όλο.',
        },
        never_the_same: {
          eyebrow: 'Σχετικά μ’ αυτό',
          title: 'Δεν θα ζήσετε δύο φορές το ίδιο βράδυ.',
          body: 'Χτισμένο από τη διάθεση, τον χρόνο και τα όριά σας — ποτέ δύο φορές το ίδιο.',
        },
        not_an_interview: {
          eyebrow: 'Σχετικά μ’ αυτό',
          title: 'Κανείς δεν δίνει συνέντευξη.',
          body: 'Κανείς σας δεν είναι αυτός που ρωτάει.',
        },
        general: {
          eyebrow: 'Γιατί λειτουργεί',
          title: 'Κανείς σας δεν χρειάζεται να πάρει την πρωτοβουλία.',
          body: 'Τη στιγμή την κρατάει η ερώτηση, όχι ένας από τους δυο σας.',
        },
      },
    },
  },

  howItWorks: {
    howitworks_together: {
      eyebrow: 'Πώς λειτουργεί · 1',
      title: 'Διαλέξτε τη διάθεση. Στείλτε τους έναν σύνδεσμο.',
      body: 'Μπαίνουν από το κινητό τους. Χωρίς λογαριασμό, χωρίς εγκατάσταση.',
      cta: 'Και μετά;',
    },
    howitworks_moment: {
      eyebrow: 'Πώς λειτουργεί · 2',
      title: 'Ένας διαφορετικός κόσμος για κάθε είδος εγγύτητας.',
      body: 'Απαντήστε ή προσπεράστε. Σημειώστε ό,τι σας αγγίζει.',
      cta: 'Κατάλαβα',
    },
  },

  insight: {
    eyebrow: 'Το τελετουργικό σας παίρνει μορφή',
    chartLabel: 'Προτεινόμενη ισορροπία ερωτήσεων',
    note: 'Μια πρόταση από τις επιλογές σας — όχι βαθμολογία.',
    cta: 'Συνεχίστε',
    labels: { playful: 'Παιχνιδιάρικα', warm: 'Ζεστά', deep: 'Βαθιά', bold: 'Τολμηρά' },
    goals: {
      fun: {
        title: 'Το παιχνίδι ανοίγει την πόρτα.',
        body: 'Ξεκινάει ελαφρύ, μετά μια πιο ζεστή στιγμή.',
      },
      closeness: {
        title: 'Η εγγύτητα ξεκινά απαλά.',
        body: 'Πρώτα ζεστασιά, μετά αυτά που αξίζει να ειπωθούν.',
      },
      understanding: {
        title: 'Η άνεση προηγείται της χημείας.',
        body: 'Ζεστά και παιχνιδιάρικα, χωρίς πίεση για εντυπώσεις.',
      },
      growth: {
        title: 'Το βάθος λειτουργεί καλύτερα όταν έχει κερδηθεί.',
        body: 'Χτίζει σταδιακά, ώστε το βάθος να μοιάζει κερδισμένο.',
      },
    },
  },

  crafting: {
    eyebrow: 'Μια στιγμή',
    title: 'Φτιάχνουμε το τελετουργικό σας…',
    body: 'Διαμορφώνουμε κάθε ερώτηση γύρω από αυτά που μόλις μας είπατε.',
    goal: 'Διαβάζουμε τον στόχο σας — {{value}}',
    context: 'Το προσαρμόζουμε για {{value}}',
    energy: 'Ταιριάζουμε την ενέργειά σας — {{value}}',
    boundaries_one: 'Σεβόμαστε το όριό σας — {{count}} ορισμένο',
    boundaries_other: 'Σεβόμαστε τα όριά σας — {{count}} ορισμένα',
    depth: 'Ορίζουμε τον ρυθμό σας — {{value}}',
  },

  gate: {
    eyebrow: 'Έτοιμο',
    title: 'Το τελετουργικό σας είναι έτοιμο.',
    body: 'Πού να το στείλουμε;',
    google: 'Συνέχεια με Google',
    or: 'ή με το email σας',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    name: 'Πώς να σας λέμε;',
    cta: 'Δείξτε μου το τελετουργικό μου',
    working: 'Αποθηκεύουμε το τελετουργικό σας…',
    note: 'Χωρίς κωδικό. Θα σας στείλουμε σύνδεσμο σύνδεσης.',
    error: 'Δεν μπορέσαμε να αποθηκεύσουμε το τελετουργικό σας. Δοκιμάστε ξανά.',
    invalidEmail: 'Αυτό δεν μοιάζει με έγκυρη διεύθυνση email.',
    errorRateLimited: 'Αρκετές προσπάθειες στη σειρά — περιμένετε ένα λεπτό και δοκιμάστε ξανά.',
    errorOffline: 'Φαίνεται πως είστε εκτός σύνδεσης. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',
    errorUnavailable: 'Δεν μπορούμε να το αποθηκεύσουμε αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο.',
  },

  result: {
    eyebrow: 'Το πρώτο σας τελετουργικό είναι έτοιμο',
    reflectEyebrow: 'Φτιαγμένο από τις απαντήσεις σας',
    reflectBody: 'Φτιαγμένο από αυτά που μόλις μας είπατε.',
    sample: 'Η πρώτη σας ερώτηση',
    mix: 'Το μείγμα ερωτήσεών σας',
    minutes: 'λεπτά',
    yourPace: 'Ο ρυθμός σας',
    lighter: 'Κάντε το πιο ελαφρύ',
    deeper: 'Πάμε πιο βαθιά',
    whyTitle: 'Γιατί λειτουργεί',
    whyBody: 'Ερωτήσεις που ανοίγονται σταδιακά, με απαντήσεις κι από τους δύο. Τον ρυθμό τον ορίζετε εσείς· η παράλειψη επιτρέπεται πάντα.',
    transparent: 'Μια πρόταση από τις επιλογές σας — όχι βαθμολογία προσωπικότητας, συμβατότητας ή ψυχολογικής αξιολόγησης.',
    cta: 'Αποθήκευση του τελετουργικού μου',
    paces: { gentle: 'ήπιος ρυθμός', bold: 'τολμηρός ρυθμός', balanced: 'ισορροπημένος ρυθμός' },
    depths: { light: 'Ελαφριά', playful: 'Παιχνιδιάρικα', deep: 'Βαθιά', bold: 'Τολμηρά' },
    contexts: {
      partner: 'Το ταίρι σας',
      date: 'Ένα ραντεβού',
      new_person: 'Κάποιος καινούριος',
      friend: 'Ένας φίλος',
      unsure: 'Όποιος είναι μαζί σας',
    },
    boundaries_one: '{{count}} θέμα εκτός',
    boundaries_other: '{{count}} θέματα εκτός',
    barrierAnswer: {
      questions: 'Είπατε ότι δεν ξέρετε ποτέ τι να ρωτήσετε. Δεν θα χρειαστεί.',
      distraction: 'Είπατε ότι τα κινητά μπαίνουν εμπόδιο. Αυτό βάζει την ερώτηση μπροστά και μετά κάνει στην άκρη.',
      time_energy: 'Είπατε ότι ο χρόνος και η ενέργεια είναι λίγα. Αυτό χωράει σε {{minutes}} λεπτά.',
      repetition: 'Είπατε ότι οι κουβέντες επαναλαμβάνονται. Αυτή δεν θα είναι δύο φορές η ίδια.',
      awkward_vulnerability: 'Είπατε ότι το να ανοιχτείτε φέρνει αμηχανία. Τίποτα εδώ δεν πάει πιο μακριά απ’ όσο επιτρέπετε κι οι δύο.',
      none: '',
    },
  },

  offer: {
    eyebrow: 'Προσφορά ιδρυτικού μέλους',
    title: 'Κρατήστε κάθε τρόπο να έρθετε πιο κοντά',
    body: 'Το τελετουργικό σας αποθηκεύτηκε. Προσθέστε τώρα το Premium Pass — μία πληρωμή, δικό σας για έναν χρόνο, και το ταίρι σας μπαίνει πάντα δωρεάν.',
    save: '20% έκπτωση σήμερα',
    oneTime: 'Εφάπαξ πληρωμή · δεν ανανεώνεται αυτόματα',
    taxNote: 'Συμπεριλαμβάνεται ΦΠΑ. Το τελικό ποσό επιβεβαιώνεται στο ταμείο.',
    buy: 'Αποκτήστε το Premium',
    freeBadge: 'ΔΩΡΕΑΝ',
    freeEyebrow: 'Η κάρτα σας το ξεκλείδωσε',
    freeTitle: 'Το Premium σας το κερνάμε',
    freeBody: 'Η κάρτα σας περιλαμβάνει 3 μήνες Bonded Premium — χωρίς πληρωμή. Ενεργοποιήστε το και θα προστεθεί στον λογαριασμό που μόλις δημιουργήσατε.',
    activateFree: 'Ενεργοποίηση δωρεάν Premium',
    haveCode: 'Έχετε κωδικό από την κάρτα σας;',
    codePlaceholder: 'BONDED-XXXX-XXXX-XXXX',
    redeem: 'Εξαργύρωση κωδικού',
    redeemError: 'Δεν μπορέσαμε να εξαργυρώσουμε αυτόν τον κωδικό. Ελέγξτε τον και δοκιμάστε ξανά.',
    redeemed: 'Το δωρεάν Premium σας είναι ενεργό. Ανοίγουμε το Bonded…',
    later: 'Ίσως αργότερα — απλώς πάρτε την εφαρμογή',
    secure: 'Ασφαλής πληρωμή από την Paddle',
    unavailable: 'Το ταμείο ανοίγει σύντομα. Μπορείτε ακόμα να εξαργυρώσετε κωδικό κάρτας ή να πάρετε την εφαρμογή τώρα.',
    benefits: {
      experiences: 'Κάθε νέα Εμπειρία',
      rituals: 'Καθοδηγούμενα τελετουργικά για κάθε διάθεση',
      memories: 'Κάψουλες Μνήμης που αξίζει να κρατήσετε',
      insights: 'Insights από τον χρόνο σας μαζί',
      hosting: 'Απεριόριστη φιλοξενία',
      partner: 'Το ταίρι σας μπαίνει δωρεάν',
    },
  },

  waitlist: {
    eyebrow: 'Είστε μέσα',
    title: 'Είστε στη λίστα.',
    position: 'Είστε ο αριθμός {{position}}',
    positionUnknown: 'Κρατήσαμε τη θέση σας.',
    body: 'Θα σας στείλουμε email μόλις ανοίξει το Bonded.',
    savedTitle: 'Τι κρατήσαμε για εσάς',
    deck: {
      eyebrow: 'Όσο περιμένετε',
      title: 'Η εφαρμογή έρχεται. Η πρώτη σας κουβέντα όχι.',
      title2: 'The Closeness Deck',
      body: '270+ ερωτήσεις, προκλήσεις και τελετουργικά για ζευγάρια. PDF 60 σελίδων, άμεση λήψη.',
      cta: 'Αποκτήστε το deck',
      note: 'Ανοίγει το κατάστημά μας σε νέα καρτέλα.',
    },
    skip: 'Ίσως αργότερα — απλώς ειδοποιήστε με',
  },

  handoff: {
    title: 'Ελέγξτε το email σας',
    body: 'Ο σύνδεσμος σύνδεσης και η θέση σας στη λίστα είναι καθ’ οδόν.',
    waitTitle: 'Το τελετουργικό σας αποθηκεύτηκε',
    open: 'Άνοιγμα στο Bonded',
    store: 'Κατεβάστε το από το App Store',
    storeAndroid: 'Αποκτήστε το στο Google Play (πρώιμη πρόσβαση Android)',
    email: 'Ένας ασφαλής σύνδεσμος σύνδεσης είναι καθ’ οδόν.',
  },

  errors: {
    generic: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.',
    offline: 'Φαίνεται πως είστε εκτός σύνδεσης. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',
    expired: 'Αυτή η συνεδρία έληξε. Ξεκινάμε νέα…',
  },
}
